<script setup>
// SiteUp (Lia Imobiliária): detalhe de um imóvel dentro do modal de sugestão.
// Galeria com todas as fotos, infos completas e ações (inserir/copiar/ver no site).
// As setas ‹ › de imóvel são emitidas pro pai (que navega inclusive entre páginas).
import { ref, computed, watch } from 'vue';

const props = defineProps({
  item: { type: Object, required: true },
  hasPrev: { type: Boolean, default: false },
  hasNext: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'prev', 'next', 'insert', 'copy', 'site']);

const photoIndex = ref(0);

const fotos = computed(() => {
  const list = Array.isArray(props.item.fotos) ? props.item.fotos.filter(Boolean) : [];
  if (list.length) return list;
  return props.item.foto ? [props.item.foto] : [];
});

watch(
  () => props.item,
  () => {
    photoIndex.value = 0;
  }
);

const formatBRL = value => {
  if (value === null || value === undefined || value === '') return 'Sob consulta';
  return `R$ ${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
};

const local = computed(() =>
  [props.item.bairro, props.item.cidade].filter(Boolean).join(', ')
);

const specs = computed(() => {
  const i = props.item;
  const parts = [];
  if (i.dormitorios) parts.push(`${i.dormitorios} dorm`);
  if (i.suites) parts.push(`${i.suites} suíte(s)`);
  if (i.banheiros) parts.push(`${i.banheiros} banh.`);
  if (i.garagem) parts.push(`${i.garagem} vaga(s)`);
  if (i.area) parts.push(`${Math.round(i.area)}m² priv.`);
  else if (i.area_total) parts.push(`${Math.round(i.area_total)}m²`);
  return parts;
});

const prevPhoto = () => {
  const n = fotos.value.length;
  if (n) photoIndex.value = (photoIndex.value - 1 + n) % n;
};
const nextPhoto = () => {
  const n = fotos.value.length;
  if (n) photoIndex.value = (photoIndex.value + 1) % n;
};
</script>

<template>
  <div class="flex flex-col h-full">
    <div
      class="flex items-center justify-between gap-2 px-4 py-3 border-b border-n-weak"
    >
      <button
        type="button"
        class="flex items-center gap-1 text-sm text-n-slate-11 hover:text-n-slate-12"
        @click="emit('close')"
      >
        <span class="i-ri-arrow-left-line size-4" /> Voltar
      </button>
      <div class="flex items-center gap-1">
        <button
          type="button"
          class="p-1 rounded-md border border-n-weak disabled:opacity-40 hover:bg-n-alpha-2"
          :disabled="!hasPrev"
          aria-label="Imóvel anterior"
          @click="emit('prev')"
        >
          <span class="i-ri-arrow-left-s-line size-4" />
        </button>
        <button
          type="button"
          class="p-1 rounded-md border border-n-weak disabled:opacity-40 hover:bg-n-alpha-2"
          :disabled="!hasNext"
          aria-label="Próximo imóvel"
          @click="emit('next')"
        >
          <span class="i-ri-arrow-right-s-line size-4" />
        </button>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto min-h-0">
      <!-- Galeria -->
      <div class="relative bg-n-alpha-2 aspect-[4/3] flex items-center justify-center">
        <img
          v-if="fotos.length"
          :src="fotos[photoIndex]"
          :alt="item.titulo"
          class="w-full h-full object-contain"
        />
        <span v-else class="i-ri-image-line size-10 text-n-slate-10" />
        <template v-if="fotos.length > 1">
          <button
            type="button"
            class="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70"
            aria-label="Foto anterior"
            @click="prevPhoto"
          >
            <span class="i-ri-arrow-left-s-line size-5" />
          </button>
          <button
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70"
            aria-label="Próxima foto"
            @click="nextPhoto"
          >
            <span class="i-ri-arrow-right-s-line size-5" />
          </button>
          <span
            class="absolute bottom-2 right-2 text-xs px-2 py-0.5 rounded-full bg-black/60 text-white"
          >
            {{ photoIndex + 1 }}/{{ fotos.length }}
          </span>
        </template>
      </div>

      <!-- Miniaturas -->
      <div
        v-if="fotos.length > 1"
        class="flex gap-1.5 px-4 py-2 overflow-x-auto border-b border-n-weak"
      >
        <button
          v-for="(foto, idx) in fotos"
          :key="idx"
          type="button"
          class="w-14 h-12 flex-none rounded-md overflow-hidden border"
          :class="idx === photoIndex ? 'border-n-brand' : 'border-n-weak'"
          @click="photoIndex = idx"
        >
          <img :src="foto" :alt="`Foto ${idx + 1}`" class="w-full h-full object-cover" />
        </button>
      </div>

      <!-- Infos -->
      <div class="p-4 flex flex-col gap-3">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h3 class="text-base font-semibold text-n-slate-12">{{ item.titulo }}</h3>
            <p v-if="local" class="text-sm text-n-slate-11 mt-0.5">
              <span class="i-ri-map-pin-line size-3.5 align-text-bottom" /> {{ local }}
            </p>
            <p v-if="item.endereco" class="text-xs text-n-slate-10 mt-0.5">
              {{ item.endereco }}
            </p>
          </div>
          <span class="text-lg font-medium text-n-teal-11 whitespace-nowrap">{{
            formatBRL(item.valor)
          }}</span>
        </div>

        <div v-if="specs.length" class="flex flex-wrap gap-1.5">
          <span
            v-for="(spec, idx) in specs"
            :key="idx"
            class="text-xs px-2 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-12"
          >
            {{ spec }}
          </span>
        </div>

        <p v-if="item.condominio" class="text-xs text-n-slate-11">
          Condomínio: {{ formatBRL(item.condominio) }}
        </p>
        <p v-if="item.mobilia" class="text-xs text-n-slate-11">
          Mobília: {{ item.mobilia }}
        </p>

        <div v-if="item.amenidades && item.amenidades.length" class="flex flex-wrap gap-1.5">
          <span
            v-for="(am, idx) in item.amenidades"
            :key="idx"
            class="text-xs px-2 py-0.5 rounded-full border border-n-weak text-n-slate-11"
          >
            {{ am }}
          </span>
        </div>

        <p
          v-if="item.descricao"
          class="text-sm text-n-slate-11 whitespace-pre-line leading-relaxed"
        >
          {{ item.descricao }}
        </p>

        <p v-if="item.fonte" class="text-xs text-n-slate-10">
          Fonte: {{ item.fonte }}
        </p>
      </div>
    </div>

    <!-- Ações -->
    <div class="flex flex-wrap gap-2 px-4 py-3 border-t border-n-weak">
      <button
        type="button"
        class="text-xs px-2.5 py-1.5 rounded-md bg-n-brand text-white hover:opacity-90"
        @click="emit('insert')"
      >
        Inserir na resposta
      </button>
      <button
        type="button"
        class="text-xs px-2.5 py-1.5 rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
        @click="emit('copy')"
      >
        Copiar link
      </button>
      <button
        type="button"
        class="text-xs px-2.5 py-1.5 rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
        @click="emit('site')"
      >
        Ver no site
      </button>
    </div>
  </div>
</template>
